import { Agent } from "../../../lib/web/core/agent";
import { Injectable } from "../../../lib/web/reflection/injectable";

const FAIRPLAY_LEGACY_KEY_SYSTEM: string = 'com.apple.fps.1_0';
const FAIRPLAY_MODERN_KEY_SYSTEM: string = 'com.apple.fps';

@Injectable({ type: 'singleton', token: 'MEDIA_SERVICE_TOKEN'})
export class MediaService {

    // Does this browser really drive FairPlay through the legacy WebKit API? Checking that
    // WebKitMediaKeys exists is not enough: current Safari exposes both APIs, and the legacy one is
    // the path that works against Axinom. Only a Safari that already dropped it needs modern EME.
    public supportsLegacyFairplay(): boolean {
        try {
            const webKitMediaKeys: any = (window as any).WebKitMediaKeys;
            return typeof webKitMediaKeys !== 'undefined'
                && typeof webKitMediaKeys.isTypeSupported === 'function'
                && webKitMediaKeys.isTypeSupported(FAIRPLAY_LEGACY_KEY_SYSTEM, 'video/mp4');
        }
        catch {
            return false;
        }
    }

    public async getSource(manifests: { type: string, url: string, drm: any }[]): Promise<{ src: string, type: string, keySystems?: any }> {
        const manifest: { type: string, url: string, drm?: any } = this.getManifest(manifests);
        if (manifest) {
            const keySystems: any = await this.getKeySystems(manifest.drm);
            const source: any = {
                src: manifest.url,
                type: this.getManifestMimeType(manifest.type),
                keySystems
            };
            return source;
        }
        else {
            return null;
        }
    }

    public getManifest(manifests?: { type: string, url: string, drm?: any }[], forChromecast?: boolean): { type: string, url: string } {
        if (!manifests?.length) {
            return null;
        }
        const preferredType: string = forChromecast || !(Agent.safari() || Agent.iOS()) ? 'dash' : 'hls';
        if (preferredType === 'dash') {
            //Only for test playready on Edge
            // if (Agent.edge() && manifests.some(m => m.type === preferredType && m.drm?.type === 'playready')) {
            //     return manifests.find(m => m.type === preferredType && m.drm?.type === 'playready');
            // }
            return manifests.find(m => m.type === preferredType && (!m.drm?.type || m.drm?.type === 'widevine'));
        }
        return manifests.find(m => m.type === preferredType) || manifests[0];
    }

    private getManifestMimeType(type: string): string {
        return type === 'hls' ? 'application/x-mpegURL' : 'application/dash+xml';
    }

    private async getKeySystems(drm: any): Promise<any> {
        if (!drm) { 
            return undefined;
        }
        const fairplayKeySystem: string = this.supportsLegacyFairplay() ? FAIRPLAY_LEGACY_KEY_SYSTEM : FAIRPLAY_MODERN_KEY_SYSTEM;
        const keySystem: string = drm.type === 'widevine' ? 'com.widevine.alpha' : drm.type === 'playready' ? 'com.microsoft.playready.recommendation' : fairplayKeySystem;
        if (drm.type === 'playready') {
            const serverCertificate = drm.certificateUrl ? await this.fetchServerCertificate(drm.certificateUrl) : undefined;
            return {
                [keySystem]: {
                    licenseUri: drm.licenseUrl,
                    serverCertificate: serverCertificate,
                }
            };
        }     
        else {   
            return {
                [keySystem]: drm.type === 'fairplay'
                    ? {
                        licenseUri: drm.licenseUrl,
                        certificateUri: drm.certificateUrl,
                        // Our key URI is skd://<kid>:<iv>. videojs-contrib-eme's default takes the
                        // content id from the URI's hostname, and the colon makes the browser read
                        // the iv as a port: it is not numeric, the URL fails to parse and the id
                        // comes out empty. Strip the scheme and keep the rest.
                        getContentId: (_emeOptions: any, initData: string) => initData.replace(/^.*:\/\//, ''),
                    }
                    : drm.licenseUrl,
            };
        }
    }
    
    private async fetchServerCertificate(certificateUrl: string): Promise<Uint8Array> {
        try {
            const response: Response = await fetch(certificateUrl);
            const arrayBuffer: ArrayBuffer = await response.arrayBuffer();
            return new Uint8Array(arrayBuffer);
        } 
        catch (e: any) {
            LOG.error('Error fetching PlayReady server certificate:', e);
            return undefined;
        }
    }
}